import * as React from 'react';
import {useEffect, useRef} from 'react';
import {v4 as uuid} from 'uuid';
import {workingConfig} from "../../main/pages/configChange/ConfigSignal";
import {CompanyConfiguration} from "../../../generated/api";
import {Checkbox} from "semantic-ui-react";
import {PdfViewer} from "./PdfViewer";


export interface ConverterAidedFileViewerProps {
    blob: any,
    fileName: string
}

const SELENIUM_SUPPORTED_EXTENSIONS = ['.html', '.htm', '.eml', '.esg', '.mail', '.jsonmail'];

function isSeleniumSupported(fileName: string): boolean {
    const lowerFileName = fileName?.toLowerCase() || '';
    return SELENIUM_SUPPORTED_EXTENSIONS.some(ext => lowerFileName.endsWith(ext));
}

export function ConverterAidedFileViewer(props: ConverterAidedFileViewerProps) {

    const [loading, setLoading] = React.useState(true);
    const [pdfData, setPdfData] = React.useState(null);
    const [pdfDataWithFilter, setPdfDataWithFilter] = React.useState(null);

    const [displayData, setDisplayData] = React.useState(null);

    const [filterExists, setFilterExists] = React.useState(false);
    const [withPagesContainingKeywordsFilter, setWithPagesContainingKeywordsFilter] = React.useState(true);
    const [useSeleniumChrome, setUseSeleniumChrome] = React.useState(false);

    const showSeleniumToggle = isSeleniumSupported(props.fileName);
    const isMounted = useRef(true);


    useEffect(() => {
        isMounted.current = true;
        setLoading(true);
        let filter = getPagesContainingKeywordsFilter();

        callConverter(false, false).then(r => {
            console.log("[ConverterAidedFileViewer] Converter has finished converting file to pdf.");
        });
        if (filter) {
            callConverter(true, false).then(r => {
                console.log("[ConverterAidedFileViewer] Converter has finished converting file to filtered pdf.");
            });
        }

        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        console.log("[ConverterAidedFileViewer] pdfData changed: ", pdfData?.length);
        if (!filterExists) {
            setDisplayData(withPagesContainingKeywordsFilter ? pdfDataWithFilter : pdfData)
            setLoading(false);
        }
    }, [pdfData]);

    useEffect(() => {
        console.log("[ConverterAidedFileViewer] pdfDataWithFilter changed: ", pdfDataWithFilter?.length);
        setDisplayData(withPagesContainingKeywordsFilter ? pdfDataWithFilter : pdfData)
        setLoading(false);
    }, [pdfDataWithFilter]);


    function getPagesContainingKeywordsFilter() {
        let config = JSON.parse(workingConfig.value) as CompanyConfiguration;
        let filter = config.genericAiConfig?.pagesContainingKeywordsFilter
        setFilterExists(filter != null)
        setWithPagesContainingKeywordsFilter(filter != null)
        return filter
    }

    async function callConverter(withFilter: boolean, selenium: boolean) {

        let url = '/converter/fileToPdf'

        let xReqId = uuid()

        let fileData = await props.blob;
        let reader = new FileReader();
        reader.readAsDataURL(fileData);

        reader.onloadend = async () => {
            let base64data = reader.result.toString().split('base64,')[1];

            const config = JSON.parse(workingConfig.value)?.genericAiConfig;

            const body = {
                base64Data: base64data,
                fileName: props.fileName,
                pagesContainingKeywordsFilter: withFilter ? config?.pagesContainingKeywordsFilter : null,
                html2PdfUsingSeleniumAndChrome: selenium
            };

            let response = await fetch(url, {
                method: 'POST',
                headers: {
                    "Content-Type": "application/json; charset=utf-8",
                    'x-request-id': xReqId
                },
                body: JSON.stringify(body)
            })

            if (response.status !== 200) {
                console.error("Error converting file to pdf. Status: ", response.status);
                return
            }

            let data = await response.blob();

            if (withFilter) {
                setPdfDataWithFilter(data);
            } else {
                setPdfData(data);
            }
            setLoading(false);
        };
    }

    function handleSeleniumToggle() {
        const newValue = !useSeleniumChrome;
        setUseSeleniumChrome(newValue);
        setLoading(true);

        callConverter(false, newValue).then(r => {
            console.log("[ConverterAidedFileViewer] Converter has finished converting file to pdf with selenium: " + newValue);
        });
        if (filterExists) {
            callConverter(true, newValue).then(r => {
                console.log("[ConverterAidedFileViewer] Converter has finished converting file to filtered pdf with selenium: " + newValue);
            });
        }
    }

    function displayPdfWithFilterToggle() {
        console.log("[ConverterAidedFileViewer] Displaying pdf with filter toggle. displayData set: ", displayData != null)
        return <>
            {filterExists && <Checkbox
                style={{
                    marginLeft: 20,
                    backgroundColor: 'rgba(38,153,140,0.13)',
                    borderRadius: '10px',
                    paddingRight: '10px',
                    width: 'fit-content'
                }}
                type='checkbox'
                toggle
                checked={withPagesContainingKeywordsFilter}
                onChange={(e) => {
                    setWithPagesContainingKeywordsFilter(!withPagesContainingKeywordsFilter)
                    setDisplayData(withPagesContainingKeywordsFilter ? pdfData : pdfDataWithFilter)

                }}
                label={"Show only pages containing keywords"}
            />}
            {showSeleniumToggle && <Checkbox
                style={{
                    marginLeft: 20,
                    backgroundColor: 'rgba(38,153,140,0.13)',
                    borderRadius: '10px',
                    paddingRight: '10px',
                    width: 'fit-content'
                }}
                type='checkbox'
                toggle
                checked={useSeleniumChrome}
                onChange={handleSeleniumToggle}
                label="Use Selenium Chrome"
            />}
            {displayData && <PdfViewer blob={displayData} fileName={props.fileName}/>}
        </>
    }

    return <>
        {loading && <div>Loading...</div>}
        {!loading && displayPdfWithFilterToggle()}
    </>
}